import { logError } from '@core/logging';
import { AnyUseBaseQueryOptions, OmitKeyof, QueryFunction, QueryKey, queryOptions, UseQueryOptions } from '@tanstack/react-query';
import { serializeError } from 'serialize-error';
import { ApiResponse, BaseApiResponse, HttpResponse } from './Api';
import type { ApiClient } from './runtime/apiRuntime';
import { apiOf } from './runtime/binding';
import { isBaseApiResponse, unwrapApiResponse } from './unwrap';

export type ApiQueryCall<TData> = (api: ApiClient) => Promise<HttpResponse<ApiResponse<TData>, BaseApiResponse>>;
export type BaseApiQueryOptions = Partial<AnyUseBaseQueryOptions>;

/**
 * What an OptionsApi entry hands to useQuery, useSuspenseQuery and ensureQueryData. The caller's
 * loose options are merged in, which would otherwise widen queryFn to include skipToken and make
 * the whole thing unusable for useSuspenseQuery; every entry therefore lands on this one shape.
 */
export type ApiQueryOptions<TData, TQueryKey extends QueryKey = QueryKey> = OmitKeyof<UseQueryOptions<TData, BaseApiResponse, TData, TQueryKey>, 'queryFn'> & {
    queryFn?: QueryFunction<TData, TQueryKey, never>;
};

/**
 * Query options for an endpoint. The key is plain; the QueryClient that runs the query keeps it
 * apart per identity (see runtime/queryClient.ts).
 */
export function apiQueryOptions<TData, TQueryKey extends QueryKey = QueryKey>(queryKey: TQueryKey, call: ApiQueryCall<TData>, options?: BaseApiQueryOptions) {
    return queryOptions<TData, BaseApiResponse, TData, TQueryKey>({
        queryKey,
        // The Api comes from the QueryClient running this query, never from anything ambient: that
        // client belongs to exactly one request, so these are that request's credentials. The data
        // is unpacked here because SSR cannot dehydrate an HttpResponse.
        queryFn: async ({ client }) => {
            try {
                return await unwrapApiResponse(call(apiOf(client)));
            } catch (error) {
                // A 4xx envelope is an answer -- not signed in, not found, rejected input -- and
                // reaches the screen as one. Only what the backend or the network broke belongs in
                // Seq as an error, and anything that is not an envelope at all is such a break:
                // unwrapApiResponse turns every known failure into one, so whatever gets here
                // instead came from the queryFn itself and would otherwise vanish silently.
                if (!isBaseApiResponse(error) || error.statusCode >= 500) {
                    logError('API query error', { error: serializeError(error), queryKey });
                }
                throw error;
            }
        },
        ...options
    }) as ApiQueryOptions<TData, TQueryKey>;
}
